import EventEmitter from 'eventemitter3';
import { ITab } from '../../ts/components/common/advancedTabs/AdvancedTabs';
import { IManningActivitySummary } from '../models/ManningActivitySummary';
import { IReadingList } from '../models/ReadingList';
import { ITaggedProductList } from '../models/TaggedProductList';
import { ManningOnlineProvider } from "../providers/manningOnlineProvider/ManningOnlineProvider";
import { cancelable } from 'cancelable-promise';
import ValueUtils from '../../ts/utils/ValueUtils';
import { CategorySearchFilter, ProductTypeSearchFilter, SearchFilter } from '../../search/models/SearchFilter';
import { IProductDescription } from '../../ts/models/product/IProductDescription';
import { ReadingListProvider } from '../providers/readingList/ReadingListProvider';
import { TagsProvider } from '../providers/tags/TagsProvider';
import { ITag } from '../models/ITag';
import { ProductUtils } from '../../ts/utils/ProductUtils';
import { IOwnedProduct } from '../models/OwnedProduct';
import { INewsItem } from '../models/INewsItem';
import { IProductForum } from '../models/IProductForum';
import { ScrapbookStore } from '../../scrapbook/store/ScrapbookStore';
import { LocalStorageUtils } from '../../ts/utils/LocalStorageUtils';
import { IChatUser } from '../../chat/models/ChatUser';
//import { ChatStore } from '../../chat/store/ChatStore';
import { ITagsHostStore } from './ITagsHostStore';
import { IProductSalesInfoMap } from '../../newSearch/models/IProductSalesInfo';
import { ReadingListStore } from './ReadingListStore';
import React from 'react';
import { SearchCatalogStore } from '../../search-catalog/store/SearchCatalogStore';
import { ISearchCatalogStoreProvider } from '../../search-catalog/providers/ISearchCatalogStoreProvider';
import { SourceApp } from '../../Config';

export enum ManningOnlineCatalogSortOrder {
    Default,
    Newest
}

export class ManningOnlineStore implements ITagsHostStore {
    private changeEmitter: EventEmitter;
    
    public provider: ManningOnlineProvider;
    private tagsProvider: TagsProvider;
        
    public dummyProduct: IProductDescription;

    public scrapbookStore: ScrapbookStore;
    //public chatStore: ChatStore;
    public readingListStore: ReadingListStore;

    public defaultLibraryProducts: "all" | "books" | "videos" | "liveprojects" | "purchased" | "meap" | "audio";
    public subscriptionsEnabled: boolean = false;

    public username: string;
    public subscriptionType: "LITE" | "PRO" | "TEAM";
    public isSubscriptionOwner: boolean;
    public subscriptionId: number;

    public catalogState: {
        tabs: ITab[];
        selectedTab: ITab;

        news: INewsItem[];
        recentReading: IProductDescription[];
        recommendedReading: IProductDescription[];
        
        ownedProducts: IOwnedProduct[];
        allProducts: IProductDescription[];

        isFilterSelected: boolean;
        isSortSelected: boolean;

        categoryFilters: CategorySearchFilter[];
        productTypeFilters: ProductTypeSearchFilter[];
        
        selectedSearchFilters: SearchFilter[];

        sortOrder: ManningOnlineCatalogSortOrder;

        coversAreDraggable: boolean;
        numberOfUsers: number;

        credits: number;

        productSalesInfoMap: IProductSalesInfoMap;

        useSearchCatalog: boolean;
    };

    public manningActivityState: {
        isLoading: boolean; 
        manningActivitySummary: IManningActivitySummary;
    }

    public desktopState: {
        isDesktopShown: boolean;
        isMaximized: boolean;
        defaultHeight: string;
    }

    public tourState: {
        hasSeenIntroStartModal: boolean;
        hasSeenIntroSteps: boolean;
        hasSeenIntroEndModal: boolean;
        hasSeenDesktopTour: boolean;
        hasSeenChatTour: boolean;
        hasSeenScrapbookTour: boolean;
        hasSeenMapTour: boolean;
        
        shouldShowDesktopTour: boolean;
    }
    
    public coverPlaceholder: string = "https://lx-common-resources.s3-us-west-2.amazonaws.com/resources/cover.png";
    public wideCoverPlaceholder: string = "https://lx-common-resources.s3-us-west-2.amazonaws.com/resources/wide-cover.png";
    
    constructor(provider: ManningOnlineProvider, 
                readingListProvider: ReadingListProvider, 
                tagsProvider: TagsProvider,
                scrapbookStore: ScrapbookStore){
        this.provider = provider;
        this.tagsProvider = tagsProvider;
        //this.chatStore = chatStore;

        this.scrapbookStore = scrapbookStore;
        this.readingListStore = new ReadingListStore(readingListProvider);
        
        this.changeEmitter = new EventEmitter();

        ProductUtils.config = this.provider.config;

        const tabs: ITab[] = [{title: "latest"}, {title: "lists"}, {title: "library"}, {title: "dashboard"}];

        this.catalogState = {
            tabs: tabs,
            selectedTab: tabs[0],
            
            news: this.getNewsLoadingList(),
            recentReading: this.getRecentReadingDummyList(),
            recommendedReading: this.getRecommendedReadingsDummyList(),
            
            allProducts: this.getProductDummies(),
            ownedProducts: this.toDummyOwnedProducts(this.getProductDummies()),//this.getOwnedProductDummies(),
        
            isFilterSelected: false,
            isSortSelected: false,
            
            categoryFilters: [],
            productTypeFilters: [],
            selectedSearchFilters: [],

            sortOrder: ManningOnlineCatalogSortOrder.Default,

            coversAreDraggable: true,
            numberOfUsers: 0,
            credits: 0,

            productSalesInfoMap: {},

            useSearchCatalog: false
        };

        this.manningActivityState = {
            isLoading: true, 
            manningActivitySummary: null
        };

        this.desktopState = {
            isDesktopShown: false,
            isMaximized: false,
            defaultHeight: LocalStorageUtils.getItem("desktop-height") || "400px"
        };
        
        this.dummyProduct = ProductUtils.createDummyProduct("&nbsp;", this.coverPlaceholder);

        this.toggleCatalogFilter = this.toggleCatalogFilter.bind(this);
        this.toggleCatalogSort = this.toggleCatalogSort.bind(this);
        this.closeReadingListModal = this.closeReadingListModal.bind(this);
        this.requestNewReadingListCreationModal = this.requestNewReadingListCreationModal.bind(this);
        this.onProductCoverDragStart = this.onProductCoverDragStart.bind(this);
        this.toggleDesktop = this.toggleDesktop.bind(this);
        this.toggleScrapbook = this.toggleScrapbook.bind(this);
        this.toggleMaximizeDesktop = this.toggleMaximizeDesktop.bind(this);
        this.startChatWithUser = this.startChatWithUser.bind(this);
        this.triggerMapShowRequested = this.triggerMapShowRequested.bind(this);
        this.triggerCreditsShowRequested = this.triggerCreditsShowRequested.bind(this);
        this.triggerTeamPageRequested = this.triggerTeamPageRequested.bind(this);
        this.shareReadingList = this.shareReadingList.bind(this);
        
        this.tagsProvider.getAllTags();
        this.sideloadReadingLists();
        this.loadProductSalesInfo();

        this.tourState = {
            hasSeenIntroStartModal: false,
            hasSeenIntroSteps: false,
            hasSeenIntroEndModal: false,
            hasSeenDesktopTour: false,
            hasSeenChatTour: false,
            hasSeenMapTour: false,
            hasSeenScrapbookTour: false,
            shouldShowDesktopTour: false
        };
    }

    public searchCatalogStore: SearchCatalogStore; 
    public useSearchCatalog(provider: ISearchCatalogStoreProvider){
        this.catalogState.useSearchCatalog = true;
        this.searchCatalogStore = new SearchCatalogStore(
            this.provider.sourceApp, 
            this.provider.deploymentType,
            provider
        );
    }

    public shouldUseSearchCatalog(): boolean {
        return this.catalogState.useSearchCatalog;
    }

    public discussionsComponent: React.ReactNode;
    public setDiscussionsComponent(discussionsComponent: React.ReactNode){
        if(this.catalogState.tabs.find(it => it.title == "discussions") == null){
            this.catalogState.tabs.push({title: "discussions"});
        }

        this.discussionsComponent = discussionsComponent;
        this.triggerCatalogChange();
    }

    public toggleDesktopTour() {
        this.tourState.shouldShowDesktopTour = !this.tourState.shouldShowDesktopTour;
        this.triggerCatalogChange();
    }

    public getNewUnnamedReadingListTitle(): string {
        return this.readingListStore.getNewUnnamedReadingListTitle();
    }

    public openChat(){
        //this.chatStore.open();
    }

    public startChatWithUser(user: IChatUser){
        /*this.chatStore.open();
        this.chatStore.setPotentialInterlocutor(user);*/
    }

    private sideloadReadingLists(){
        this.readingListStore.loadReadingLists();
    }

    public updateDesktopDefaultHeight(height: number){
        LocalStorageUtils.setItem("desktop-height", `${height}px`);
    }

    public getDisplayedReadingLists(): IReadingList[] {
        return this.readingListStore.getDisplayedReadingLists();
    }
    
    public deleteReadingList(readingList: IReadingList): Promise<void> {        
        return this.readingListStore.deleteReadingList(readingList);
    }

    public toggleScrapbook(){
        this.scrapbookStore.toggleShown();
    }

    public onBeforeDesktopToggle?: () => boolean;
    public toggleDesktop(){
        if(this.onBeforeDesktopToggle != null && this.onBeforeDesktopToggle() === false) { return; }
        
        this.desktopState.isDesktopShown = !this.desktopState.isDesktopShown;
        if(!this.desktopState.isDesktopShown){
            this.desktopState.isMaximized = false;
        }
        
        this.triggerDesktopChange();
    }

    public toggleMaximizeDesktop(){
        this.desktopState.isMaximized = !this.desktopState.isMaximized;
        this.triggerDesktopChange();
    }

    public onEveryReadingListDragEnter(callback: (readingListId: number) => void) {
        this.changeEmitter.addListener("reading-list-drag-enter", callback);
    }

    public offEveryReadingListDragEnter(callback: (readingListId: number) => void) {
        this.changeEmitter.removeListener("reading-list-drag-enter", callback);
    }

    public triggerEveryReadingListDragEnter(readingListId: number) {
        this.changeEmitter.emit('reading-list-drag-enter', readingListId);
    }

    public updateReadingListTitle(readingList: IReadingList, newTitle: string): Promise<void> {
        const result = this.readingListStore.updateReadingListTitle(readingList, newTitle).finally(() => {
            this.triggerDesktopChange();
        });

        this.triggerDesktopChange();
        
        return result;
    }

    public mergeReadingLists(sourceReadingList: IReadingList, targetReadingList: IReadingList): Promise<void> {
        return this.readingListStore.mergeReadingLists(sourceReadingList, targetReadingList);
    }

    public addToReadingListByProductId(readingList: IReadingList, productId: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.provider.getProductDescription(productId).then(productDescription => {
                const result = this.readingListStore.addProductToReadingList(productDescription, readingList);

                result.finally(() => {
                    readingList.isSyncing = false;
                    this.triggerDesktopChange();
                });
            });
            
            readingList.isSyncing = true;
    
            this.triggerDesktopChange();
        });
    }

    public updateReadingListPosition(readingList: IReadingList, x: number, y: number) {
        this.readingListStore.updateReadingListPosition(readingList, x, y);
    }

    public onProductCoverDragStart(e){
        (window as any).currentlyDraggedCover = e.target;
        e.dataTransfer.setData("text", JSON.stringify({
            productId: e.target.getAttribute("data-data"),
            x: e.pageX - (window.pageXOffset + e.target.getBoundingClientRect().left),
            y: e.pageY - (window.pageYOffset + e.target.getBoundingClientRect().top)
        }));
    }

    private discussionSummaryMap: {[key: number]: IProductForum} = {};
    public getDiscussionSummary(productId: number): IProductForum {
        if(typeof this.discussionSummaryMap[productId] == "undefined"){
            this.fetchDiscussionSummary(productId);
            return { productId: -1, comments: [], isDummy: true, topicForum: "", topicForumLink: ""}
        }

        return this.discussionSummaryMap[productId];
    }

    private fetchDiscussionSummary(productId: number) {
        this.provider.getProductForum(productId).then(productForum => {
            this.discussionSummaryMap[productId] = productForum;
        }).catch(e => {
            this.discussionSummaryMap[productId] = null;
        }).finally(() => {
            this.triggerDiscussionSummaryUpdate(productId);
        });
    }

    private runningProductPercentageRequest: any;
    public getProductPercentageFinished(productId: number): Promise<number> {
        if(this.runningProductPercentageRequest){
            this.runningProductPercentageRequest.cancel && this.runningProductPercentageRequest.cancel();
        }
        
        const result: any = this.runningProductPercentageRequest = cancelable<number>(this.provider.getProductPercentageFinished(productId));

        result.finally(() => {
            this.runningProductPercentageRequest = null;
        });

        return result;
    }

    public getProductsPercentageFinished(productIds: number[]): Promise<{[productId: number]: number}> {
        const result = this.provider.getProductsPercentageFinished(productIds);

        return result;
    }

    public loadLatest(immediatlyTriggerChange: boolean = true){
        const result = this.provider.getNewsAndRecentlyRead();

        result.then(recommendations => {
            this.catalogState.news = recommendations.news;
            this.catalogState.recentReading = recommendations.recentReading;
        }).catch(e => {
        }).finally(() => {
            this.triggerCatalogChange();
        });

        if(immediatlyTriggerChange){
            this.triggerCatalogChange();
        }

        return result;
    }

    public loadRecommendedUsers(){
        /*this.chatStore.syncState().finally(() => {
            this.triggerCatalogChange();
        });*/
    }

    public loadRecommendedReadingAndOwnedProducts(immediatlyTriggerChange: boolean = true){
        const result = Promise.all([
            this.provider.getRecommendedReading(), 
            this.provider.getOwnedProducts()
        ]);

        result.then(([recommendedReading, ownedProducts]) => {
            this.catalogState.ownedProducts = ownedProducts;
            this.catalogState.recommendedReading = recommendedReading;

            try {
                const ownedProductsMap: {[key: number]: IOwnedProduct} = {};
                for(const it of ownedProducts) { 
                    ownedProductsMap[it.id] = it; 
                    it.manningId = it.id;
                }
                
                const recommendedToRemove: IProductDescription[] = [];
                for(const it of recommendedReading){
                    if(ownedProductsMap[it.manningId]){
                        recommendedToRemove.push(it);
                    }
                }

                if(this.catalogState.recentReading != null){
                    for(const it of this.catalogState.recentReading){
                        if(ownedProductsMap[it.manningId]){
                            recommendedToRemove.push(it);
                        }
                    }
                }
                

                for(const it of recommendedToRemove){
                    ValueUtils.removeItemFromArray(this.catalogState.recommendedReading, it);
                }
            }
            catch(e) {
                console.log(e);
            }
        }).finally(() => {
            this.triggerCatalogChange();
        });

        if(immediatlyTriggerChange){
            this.triggerCatalogChange();
        }

        return result;
    }

    public loadRecommendedReading(immediatlyTriggerChange: boolean = true) {
        const result = this.provider.getRecommendedReading();

        result.then(recommendedReading => {
            this.catalogState.recommendedReading = recommendedReading;
        }).finally(() => {
            this.triggerCatalogChange();
        });

        if(immediatlyTriggerChange){
            this.triggerCatalogChange();
        }

        return result;
    }

    public loadProductSalesInfo(){
        this.provider.getProductInfoSalesMap().then(productSalesInfoMap => {
            this.catalogState.productSalesInfoMap = productSalesInfoMap;
        });
    }

    public loadAllProducts(triggerImmediateChange: boolean = true): Promise<IProductDescription[]> {
        const result: any = new Promise<IProductDescription[]>((resolve, reject) => {
            Promise.all([this.provider.getAllProducts(), this.provider.getOwnedProducts(), this.provider.getLpSeriesInfo()]).then(([allProducts, ownedProducts, lpSeriesInfo]) => {
                const allProductsIdMap = {};

                for(const product of allProducts){
                    allProductsIdMap[product.manningId] = product;
                }

                if(ownedProducts && ownedProducts.length){
                    for(const product of ownedProducts){
                        product.manningId = product.id;
                        const productInfo =  allProductsIdMap[product.id];
                        if(productInfo != null){
                            if(!product.authors){ product.authors = productInfo.authors; }
                            if(!product.coverPath || (product.coverPath || "").indexOf("/undefined") >= 0){ product.coverPath = productInfo.coverPath; }
                            if(!product.link  || (product.link || "").indexOf("/undefined") >= 0){ product.link = productInfo.link; }
                            if(!product.title){ product.title = productInfo.title; }
                            if(!product.title){ product.title = productInfo.title; }
                            if(!product.externalId){ product.externalId = productInfo.externalId; }
                            if(productInfo.dateObj && +product.licenseDate == +product.productLastSignificantDate){ 
                                product.productLastSignificantDate = productInfo.dateObj;
                            }
                        }
                    }
                }

                this.catalogState.allProducts = allProducts;
                this.catalogState.ownedProducts = ownedProducts;

                this.catalogState.categoryFilters = this.deriveCategoryFiltersFromProducts(allProducts);
                this.catalogState.productTypeFilters = this.deriveProductTypeFiltersFromProducts(allProducts);
                resolve(allProducts);
            }).finally(() => {
                this.triggerCatalogChange();
            });
        });

        if(triggerImmediateChange){
            this.triggerCatalogChange();
        }

        return result;
    }

    public loadManningActivitySummary(): Promise<IManningActivitySummary> {
        this.manningActivityState.isLoading = true;
        this.manningActivityState.manningActivitySummary = null;

        const result = this.provider.getManningActivitySummary();

        result.then(data => {
            this.manningActivityState.manningActivitySummary = data;
        }).catch(e => {
            this.manningActivityState.manningActivitySummary= null;
        }).finally(() => {
            this.manningActivityState.isLoading = false;
            this.triggerManningActivityInfoChange();
        });

        this.triggerManningActivityInfoChange();

        return result;
    }

    public followTag(tag: ITag): Promise<void> {
        const result = this.tagsProvider.followTag(tag);

        result.finally(() => this.triggerTagsChange());

        this.triggerTagsChange();

        return result;
    }

    public unfollowTag(tag: ITag): Promise<void> {
        const result = this.tagsProvider.unfollowTag(tag);

        result.finally(() => this.triggerTagsChange());

        this.triggerTagsChange();

        return result;
    }

    public getAllTags(): ITag[] {
        return this.tagsProvider.allTags;
    }

    public getUserTags(): ITag[] {
        return this.tagsProvider.userTags;
    }

    public isEditDisabled(): boolean {
        return false;
    }

    public logAction(data: string) {
        this.changeEmitter.emit("logAction", data);
    }

    public onLogAction(callback: (data: string) => void){
        this.changeEmitter.addListener("logAction", callback);
    }

    public offLogAction(callback: (data: string) => void){
        this.changeEmitter.removeListener("logAction", callback);
    }

    public onDiscussionSummaryUpdated(callback: (productId: number) => void) {
        this.changeEmitter.addListener("discussions-summary", callback);
    }

    public offDiscussionSummaryUpdated(callback: (productId: number) => void) {
        this.changeEmitter.removeListener("discussions-summary", callback);
    }

    public triggerDiscussionSummaryUpdate(productId: number){
        this.changeEmitter.emit("discussions-summary", productId);
    }

    public triggerTagsChange(){
        this.changeEmitter.emit("tags");
    }

    public onTagsChange(callback: () => void){
        this.changeEmitter.addListener("tags", callback);
    }

    public offTagsChange(callback: () => void){
        this.changeEmitter.removeListener("tags", callback);
    }

    public selectLibraryTab(){
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "library"));
    }

    public selectDashboardTab(){
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "dashboard"));
    }

    public selectLibraryBooksTab(){
        this.defaultLibraryProducts = "books";
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "library"));
    }

    public selectLibraryLpTab(){
        this.defaultLibraryProducts = "liveprojects";
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "library"));
    }

    public selectLibraryLvTab(){
        this.defaultLibraryProducts = "videos";
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "library"));
    }

    public selectLibraryAudioTab(){
        this.defaultLibraryProducts = "audio";
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "library"));
    }

    public selectReadingListsTab(){
        this.selectCatalogTab(this.catalogState.tabs.find(it => it.title == "lists"));
    }

    public selectCatalogTab(tab: ITab) {
        this.catalogState.selectedTab = tab;

        if(this.isLatestCatalogTabSelected()){
            this.loadLatest(false);
        }
        else if(this.isListsCatalogTabSelected()){
            this.loadRecommendedReadingAndOwnedProducts(false);
        }
        else if(this.isLibraryTabSelected() || this.isDasboardTabSelected()){
            this.loadAllProducts(false);
            this.loadProductSalesInfo();
        }

        this.triggerCatalogChange();
        ProductUtils.sourceApp === SourceApp.demo && $(document).trigger("manning-online-tab-changed") 
    }

    public toggleCatalogFilter(){
        this.catalogState.isFilterSelected = !this.catalogState.isFilterSelected;
        if(this.catalogState.isFilterSelected && this.catalogState.isSortSelected) {
            this.catalogState.isSortSelected = false;
        }
        
        this.triggerCatalogSortFilterChange();
    }

    public toggleCatalogSort(){
        this.catalogState.isSortSelected = !this.catalogState.isSortSelected;
        if(this.catalogState.isFilterSelected && this.catalogState.isSortSelected) {
            this.catalogState.isFilterSelected = false;
        }
        
        this.triggerCatalogSortFilterChange();
    }

    public triggerCatalogSortFilterChange(){
        this.changeEmitter.emit("catalog-sort-filter-change");
    }

    public onCatalogSortFilterChange(callback: () => void){
        this.changeEmitter.addListener("catalog-sort-filter-change", callback);
    }

    public offCatalogSortFilterChange(callback: () => void){
        this.changeEmitter.removeListener("catalog-sort-filter-change", callback);
    }

    public triggerDesktopChange(){
        this.changeEmitter.emit("desktop");
    }

    public onDesktopChange(callback: () => void){
        this.changeEmitter.addListener("desktop", callback);
    }

    public offDesktopChange(callback: () => void){
        this.changeEmitter.removeListener("desktop", callback);
    }

    public triggerDesktopProductPlacementStateChange(){
        this.changeEmitter.emit("desktop-state-product-placement");
    }

    public onDesktopProductPlacementStateChange(callback: () => void){
        this.changeEmitter.addListener("desktop-state-product-placement", callback);
    }

    public offDesktopProductPlacementStateChange(callback: () => void){
        this.changeEmitter.removeListener("desktop-state-product-placement", callback);
    }

    public triggerCatalogChange(){
        this.changeEmitter.emit("catalog-change");
    }

    public onCatalogChange(callback: () => void){
        this.changeEmitter.addListener("catalog-change", callback);
    }

    public offCatalogChange(callback: () => void){
        this.changeEmitter.removeListener("catalog-change", callback);
    }

    public triggerManningActivityInfoChange(){
        this.changeEmitter.emit("manning-activity-info-change");
    }

    public onManningActivityChange(callback: () => void){
        this.changeEmitter.addListener("manning-activity-info-change", callback);
    }

    public offManningActivityChange(callback: () => void){
        this.changeEmitter.removeListener("manning-activity-info-change", callback);
    }

    public onReadingListChange(callback: () => void){
        this.readingListStore.onReadingListChange(callback);
    }

    public offReadingListChange(callback: () => void){
        this.readingListStore.offReadingListChange(callback);
    }

    public offReadingListModalChange(callback: () => void){
        this.readingListStore.offReadingListModalChange(callback);
    }

    public onMapShowRequested(callback: () => void){
        this.changeEmitter.addListener("map-show", callback);
    }

    public offMapShowRequested(callback: () => void){
        this.changeEmitter.removeListener("map-show", callback);
    }

    public triggerMapShowRequested() {
        this.changeEmitter.emit("map-show");
    }

    public onCreditsShowRequested(callback: () => void){
        this.changeEmitter.addListener("credits-show", callback);
    }

    public offCreditsShowRequested(callback: () => void){
        this.changeEmitter.removeListener("credits-show", callback);
    }

    public onTeamPageRequested(callback: () => void){
        this.changeEmitter.addListener("team-page-requested", callback);
    }

    public offTeamPageRequested(callback: () => void){
        this.changeEmitter.removeListener("team-page-requested", callback);
    }

    public triggerTeamPageRequested(){
        this.changeEmitter.emit("team-page-requested");
    }

    public triggerCreditsShowRequested(){
        this.changeEmitter.emit("credits-show");
    }

    public isLatestCatalogTabSelected(): boolean {
        return this.catalogState != null && this.catalogState.selectedTab != null && this.catalogState.selectedTab.title == "latest";
    }

    public isListsCatalogTabSelected(): boolean {
        return this.catalogState != null && this.catalogState.selectedTab != null && this.catalogState.selectedTab.title == "lists";
    }

    public isLibraryTabSelected(): boolean {
        return this.catalogState != null && this.catalogState.selectedTab != null && this.catalogState.selectedTab.title == "library";
    }

    public isDiscussionsTabSelected(): boolean {
        return this.catalogState != null && this.catalogState.selectedTab != null && this.catalogState.selectedTab.title == "discussions";
    }

    public isDasboardTabSelected(): boolean {
        return this.catalogState != null && this.catalogState.selectedTab != null && this.catalogState.selectedTab.title == "dashboard";
    }

    public closeReadingListModal(){
        this.readingListStore.closeReadingListModal();
    }

    public shareReadingList(id: number): Promise<string> {
        return this.readingListStore.shareReadingList(id);
    }

    public copySharedList(id: number): Promise<number> {
        const promise = this.readingListStore.copySharedList(id);

        promise.then((newListId: number) => {
            this.readingListStore.getReadingLists().finally(() => {
                const tab = this.catalogState.tabs.find(it => it.title == "lists");
                this.catalogState.selectedTab = tab;
                this.loadRecommendedReadingAndOwnedProducts(true).finally(() => {
                    setTimeout(() => {
                        const element = document.querySelector(`[data-tagged-product-list-id='${newListId}']`);
                        if(element != null){
                            element.scrollIntoView({
                                behavior:'smooth',
                            });
                        }
                    }, 100);
                });
            });
        });

        return promise;
    }

    public changeReadingListTitle(readingList: IReadingList, newTitle: string) {
        const result = this.readingListStore.changeReadingListTitle(readingList, newTitle);

        result.catch(e => {
            this.triggerCatalogChange();
        });

        this.triggerCatalogChange();
    }

    public requestNewReadingListCreationModal(productDecription?: IProductDescription){
        this.readingListStore.requestNewReadingListCreationModal(productDecription);
    }

    public requestReadingListSpecificationForNewsProduct(product: {id: number; manningId: number}) {
        if(product == null) { return; }

        this.provider.getProductDescription(product.id || product.manningId).then(productDescription => {
            this.requestReadingListSpecificationForProduct(productDescription);
        }).catch(e => {
            alert("Error when fetching product. Try later.");
        });
    }

    public requestReadingListSpecificationForProduct(productDescription: IProductDescription) {
        return this.readingListStore.requestReadingListSpecificationForProduct(productDescription);
    }

    public addCurrentProductToReadingList(readingListLabel: string) {
        return this.readingListStore.addCurrentProductToReadingList(readingListLabel);
    }

    public removeCurrentProductFromReadingList(readingListLabel: string) {
        return this.readingListStore.removeCurrentProductFromReadingList(readingListLabel);
    }

    public removeProductFromReadingList(readingList: IReadingList, productDescription: IProductDescription): Promise<void> {
        return this.readingListStore.removeProductFromReadingList(readingList, productDescription);
    }

    public createReadingList(title: string, position?: { x: number; y: number; }) {
        this.readingListStore.createReadingList(title, position);
    }

    public createReadingListWithProduct(productId: number, title: string, position: { x: number; y: number; }) {
        this.provider.getProductDescription(productId).then(productDescription => {            
            this.readingListStore.createReadingListWithProduct(title, productDescription, position);
        });
    }

    public createReadingListAndAddCurrentProduct(newReadingListName: string) {
        this.readingListStore.createReadingListAndAddCurrentProduct(newReadingListName);
    }

    public isInAtLeastOneReadingList(product: {id: any, manningId?: any}): boolean {
        return this.readingListStore.isInAtLeastOneReadingList(product);
    }

    public readingListNameExists(name: string): boolean {
        return this.readingListStore.readingListNameExists(name);
    }

    public getProductDescription(productId: number): Promise<IProductDescription> {
        return this.provider.getProductDescription(productId);
    }

    private getRecommendedReadingsDummyList(): IProductDescription[] {
        return [ 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true),  
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true)
        ];
    }

    private getRecentReadingDummyList(): IProductDescription[] {
        return [ 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true),  
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("book", this.coverPlaceholder, false, true)
        ];
    } 

    private getNewsLoadingList(): INewsItem[] {
        return [
            this.createDummyProductNewsItem(), 
            this.createDummyProductNewsItem(), 
            this.createDummyProductNewsItem(),
            this.createDummyProductNewsItem()
        ];
    }

    private createDummyProductNewsItem(): INewsItem {
        return {
            id: -1,
            product: {
                id: -1,
                manningId: -1,
                coverPath: this.coverPlaceholder,
                title: ProductUtils.getLoaderLines(1),
            },
            
            description: ProductUtils.getLoaderLines(2),
            link: "",
            
            title: ProductUtils.getLoaderLines(1),
            type: "meapUpdate",
            isDummy: true
        };
    }

    private getProductDummies(): IProductDescription[] {
        const demoProducts = [
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true), 
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true),
            ProductUtils.createDummyProduct("dummy", this.coverPlaceholder, false, true)
        ];

        demoProducts.forEach(it => it.dateObj = null);

        return demoProducts;
    }

    public setCatalogSortState(value: string) {
        this.catalogState.sortOrder = value == "newest" ? ManningOnlineCatalogSortOrder.Newest : ManningOnlineCatalogSortOrder.Default
        this.catalogState.isSortSelected = false;
        this.triggerCatalogChange();
    }

    public getDisplayedAllProducts(): { ownedProducts: IOwnedProduct[], allProducts: IProductDescription[]}{
        const ownedProductsMap: {[key: number]: boolean} = {};
        
        const displayedOwnedProducts = this.getDisplayedOwnedProducts();
        const displayedProducts = this.getDisplayedProducts(this.catalogState.allProducts);
 
        displayedOwnedProducts.forEach(product => ownedProductsMap[product.id] = true);

        return {
            ownedProducts: displayedOwnedProducts,
            allProducts: displayedProducts.filter(it => !ownedProductsMap[it.manningId])
        };
    }

    public getDisplayedProducts(products: IProductDescription[]): IProductDescription[] {
        return this.sortProductDescriptions(this.filterProductDescriptionsBySelectedFilters(products));
    }

    public getDisplayedOwnedProducts(): IOwnedProduct[] {
        return this.sortOwnedProducts(this.filterOwnedProductsBySelectedFilters((this.catalogState.ownedProducts || []).concat([])));
    }

    public getDisplayedTaggedProductsList(taggedProductList: ITaggedProductList): ITaggedProductList {
        return this.sortTaggedProductsList(this.filterBySelectedFilters([taggedProductList])[0]);
    }

    public getDisplayedTaggedProductsLists(taggedProductLists: ITaggedProductList[]): ITaggedProductList[] {
        return this.sortTaggedProductsLists(this.filterBySelectedFilters(taggedProductLists || []).concat([]));
    }
    
    public sortProductDescriptions(productDescriptions: IProductDescription[]): IProductDescription[] {
        if(this.catalogState.sortOrder == ManningOnlineCatalogSortOrder.Default){
            return productDescriptions;
        }

        return productDescriptions.sort(this.sortByDate);
    }

    public sortOwnedProducts(ownedProducts: IOwnedProduct[]): IOwnedProduct[] {
        if(this.catalogState.sortOrder == ManningOnlineCatalogSortOrder.Default){
            return ownedProducts;
        }        
    }

    public sortTaggedProductsLists(taggedProductLists: ITaggedProductList[]): ITaggedProductList[] {
        if(this.catalogState.sortOrder == ManningOnlineCatalogSortOrder.Default){
            return taggedProductLists;
        }

        return taggedProductLists.map(it => this.sortTaggedProductsList(it));
    }

    public sortTaggedProductsList(taggedProductList: ITaggedProductList): ITaggedProductList {
        if(this.catalogState.sortOrder == ManningOnlineCatalogSortOrder.Default){
            return taggedProductList;
        }

        return {
            title: taggedProductList.title,
            products: (taggedProductList.products || []).concat([]).sort(this.sortByDate),
            lastUpdated: taggedProductList.lastUpdated
        };
    }

    private sortByDate(a: {dateObj?: Date}, b: {dateObj?: Date}): number {
        if(b.dateObj != null && a.dateObj != null){
            return b.dateObj.getTime() - a.dateObj.getTime();
        }

        return 0;
    }

    private filterProductDescriptionsBySelectedFilters(productDescriptions: IProductDescription[]): IProductDescription[] {
        if(this.catalogState.selectedSearchFilters == null 
        || this.catalogState.selectedSearchFilters.length == 0) {
            return productDescriptions;
        }
            
        let products = (productDescriptions).concat([]);

        const searchFilterGroups = SearchFilter.groupSearchFiltersByType(this.catalogState.selectedSearchFilters);

        for (const searchFilterGroup of searchFilterGroups) {
            const alreadyAddedMap: { [key: number]: boolean } = {};
            const keptItems = [];

            for (const searchFilter of searchFilterGroup) {
                for (const item of products) {
                    if (searchFilter.matchesProductDescription(item) && !alreadyAddedMap[item.id]) {
                        alreadyAddedMap[item.id] = true;
                        keptItems.push(item);
                    }
                }
            }

            products = keptItems;
        }

        return products;
    }

    private filterOwnedProductsBySelectedFilters(ownedProducts: IOwnedProduct[]): IOwnedProduct[] {
        if(this.catalogState.selectedSearchFilters == null 
        || this.catalogState.selectedSearchFilters.length == 0) {
            return ownedProducts;
        }
        let products = ownedProducts.concat([]);
        const searchFilterGroups = SearchFilter.groupSearchFiltersByType(this.catalogState.selectedSearchFilters);

        for (const searchFilterGroup of searchFilterGroups) {
            const alreadyAddedMap: { [key: number]: boolean } = {};
            const keptItems = [];

            for (const searchFilter of searchFilterGroup) {
                for (const item of products) {
                    if (searchFilter.matchesProductDescription(item) && !alreadyAddedMap[item.id]) {
                        alreadyAddedMap[item.id] = true;
                        keptItems.push(item);
                    }
                }
            }

            products = keptItems;
        }

        return products;
    }

    private filterBySelectedFilters(taggedProductLists: ITaggedProductList[]): ITaggedProductList[] {
        if(this.catalogState.selectedSearchFilters == null 
        || this.catalogState.selectedSearchFilters.length == 0) {
            return taggedProductLists;
        }

        const searchFilterGroups = SearchFilter.groupSearchFiltersByType(this.catalogState.selectedSearchFilters);
        
        return taggedProductLists.map(taggedProductList => {
            return {
                title: taggedProductList.title,
                products: this.filterProductsBySelectedFilters(taggedProductList.products, searchFilterGroups),
                lastUpdated: taggedProductList.lastUpdated
            }
        }).filter(it => it.products.length > 0);
    }

    private filterProductsBySelectedFilters(products: IProductDescription[], searchFilterGroups: SearchFilter[][]): IProductDescription[] {
        for (const searchFilterGroup of searchFilterGroups) {
            const alreadyAddedMap: { [key: number]: boolean } = {};
            const keptItems = [];

            for (const searchFilter of searchFilterGroup) {
                for (const item of products) {
                    if (searchFilter.matchesProductDescription(item) && !alreadyAddedMap[item.id]) {
                        alreadyAddedMap[item.id] = true;
                        keptItems.push(item);
                    }
                }
            }

            products = keptItems;
        }

        return products;
    }

    public updateCatalogFilterSelection(searchFilter: SearchFilter, isSelected: boolean) {
        if(isSelected){
            if(this.catalogState.selectedSearchFilters.indexOf(searchFilter) == -1){
                this.catalogState.selectedSearchFilters.push(searchFilter);
                this.triggerCatalogChange();
            }
        }
        else {
            if(this.catalogState.selectedSearchFilters.indexOf(searchFilter) >= 0){
                ValueUtils.removeItemFromArray(this.catalogState.selectedSearchFilters, searchFilter);
                this.triggerCatalogChange();
            }
        }
    }

    private deriveProductTypeFilters(taggedProductLists: ITaggedProductList[]): ProductTypeSearchFilter[] {
        return this.deriveProductTypeFiltersFromProducts(this.getProductsFromTaggedProductLists(taggedProductLists));
    }

    private deriveProductTypeFiltersFromProducts(products: IProductDescription[]): ProductTypeSearchFilter[] {
        const processedIdMap = {};
        const productTypeCounterMap: {[key: string]: number} = {};
        const result: ProductTypeSearchFilter[] = [];

        for(const product of products) {
            if(processedIdMap[product.id]) { continue; }
            
            if(!productTypeCounterMap[product.productType]){
                productTypeCounterMap[product.productType] = 0;
            }
            
            productTypeCounterMap[product.productType]++;

            processedIdMap[product.id] = true;
        }

        const items: {productType: string, count: number}[] = [];

        for(const productType in productTypeCounterMap){
            items.push({ productType: productType, count: productTypeCounterMap[productType]});
        }

        items.sort((a, b) => b.count - a.count);

        return items.map(it => new ProductTypeSearchFilter(it.productType, it.count));
    }
    
    private deriveCategoryFilters(taggedProductLists: ITaggedProductList[]): CategorySearchFilter[] {
        return this.deriveCategoryFiltersFromProducts(this.getProductsFromTaggedProductLists(taggedProductLists));
    }

    private getProductsFromTaggedProductLists(taggedProductLists: ITaggedProductList[]): IProductDescription[] {
        if(!taggedProductLists || !taggedProductLists.length) { return []; }

        const allProducts = [];

        for(const taggedProductList of taggedProductLists){
            for(const product of taggedProductList.products) {
                allProducts.push(product);
            }
        }

        return allProducts;
    }

    private deriveCategoryFiltersFromProducts(products: IProductDescription[]): CategorySearchFilter[] {
        const processedIdMap = {};
        const categoryCounterMap: {[key: string]: number} = {};
        const result: CategorySearchFilter[] = [];

        if(!products || !products.length) { return result; }

        for(const product of products) {
            if(processedIdMap[product.id]) { continue; }
            if(!product.tags || !product.tags.length) { continue; }
            
            for(const tag of product.tags){
                if(!categoryCounterMap[tag]){
                    categoryCounterMap[tag] = 0;
                }
                
                categoryCounterMap[tag]++;
            }

            processedIdMap[product.id] = true;
        }

        const items: {category: string, count: number}[] = [];

        for(const category in categoryCounterMap){
            items.push({ category: category, count: categoryCounterMap[category]});
        }

        items.sort((a, b) => b.count - a.count);

        return items.map(it => new CategorySearchFilter(it.category, it.count));
    }

    private getOwnedProductDummies(): IOwnedProduct[]{
        return [
            this.createDummyOwnedProduct(), this.createDummyOwnedProduct(), 
            this.createDummyOwnedProduct(), this.createDummyOwnedProduct(), 
            this.createDummyOwnedProduct(), this.createDummyOwnedProduct()
        ]
    }

    private toDummyOwnedProducts(products: IProductDescription[]): IOwnedProduct[] {
        return (products || []).map(it => ({
            id: it.id,
            manningId: it.id,
            title: it.title,
            licenseDate: null,
            productLastSignificantDate: null,
            publishedDate: null,
            coverPath: this.coverPlaceholder,
            authors: it.authors,
            link: "",
            productType: "dummy",
            codeDownloadLink: "",
            forumUrl: "",
            licenses: [],
            isDummy: true
        }))
    }

    private dummyOwnedProductId: number = 0;
    private createDummyOwnedProduct(): IOwnedProduct {
        return {
            id: ++this.dummyOwnedProductId,
            title: ProductUtils.getLoaderLines(1),
            licenseDate: null,
            productLastSignificantDate: null,
            coverPath: this.coverPlaceholder,
            authors: ProductUtils.getLoaderLines(1),
            link: "",
            productType: "dummy",
            codeDownloadLink: "",
            forumUrl: "",
            licenses: [],
            isDummy: true
        };
    }

    public moveReadingListItemToNewIndex(readingList: IReadingList, productId: number, newIndex: number) {
        const result = this.readingListStore.moveReadingListItemToNewIndex(readingList, productId, newIndex).finally(() => {
            this.triggerDesktopChange();
        });

        this.triggerDesktopChange();
        
        return result;
    }
}